import { DEV_USER_EMAILS, DEV_AUTH_HEADERS } from '@wineryxplorer/shared';

/**
 * Frontend development authentication utilities
 * NEVER use in production!
 */

/**
 * Check if development authentication bypass is enabled (frontend version)
 */
const isDevAuthEnabledFrontend = (): boolean => {
  return import.meta.env.DEV && import.meta.env.VITE_ENABLE_DEV_AUTH === 'true';
};

/**
 * Set development user for API requests by email
 * This stores the email in localStorage for use in API headers
 */
export const setDevUser = (userEmail: string | null): void => {
  if (!isDevAuthEnabledFrontend()) {
    console.warn('Development auth is not enabled');
    return;
  }

  if (userEmail === null) {
    // Remove dev auth header
    localStorage.removeItem('dev-auth-user');
    // eslint-disable-next-line no-console
    console.log('🔓 Cleared development authentication');
  } else {
    // Set dev auth header with email
    localStorage.setItem('dev-auth-user', userEmail);
    // eslint-disable-next-line no-console
    console.log(`🔐 Set development user: ${userEmail}`);
  }
};

/**
 * Get current development user email
 */
export const getCurrentDevUser = (): string | null => {
  if (!isDevAuthEnabledFrontend()) {
    return null;
  }
  
  const userEmail = localStorage.getItem('dev-auth-user');
  // Validate that the email is one of the known dev user emails
  // Add defensive check in case DEV_USER_EMAILS is undefined
  if (!DEV_USER_EMAILS) {
    return userEmail;
  }
  const validEmails = Object.values(DEV_USER_EMAILS);
  return userEmail && validEmails.includes(userEmail) ? userEmail : null;
};

/**
 * Get development auth headers for tRPC requests
 * Sends the user email in the x-dev-user header
 */
export const getDevAuthHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {};
  
  if (isDevAuthEnabledFrontend()) {
    const userEmail = getCurrentDevUser();
    if (userEmail) {
      headers[DEV_AUTH_HEADERS.USER] = userEmail;
    }
  }
  
  return headers;
};

/**
 * Development auth toolbar component data
 * Returns email-based user information for the toolbar
 */
export const getDevAuthToolbarData = () => {
  if (!isDevAuthEnabledFrontend()) {
    return null;
  }

  const currentUserEmail = getCurrentDevUser();

  return {
    isEnabled: true,
    currentUserEmail,
    availableUserEmails: DEV_USER_EMAILS,
    setUser: setDevUser,
  };
};